import {lerpValues} from "_src/engine/math";

import {vec3} from "_src/libs/gl-matrix";
import {rebuildVerticesNormalsMask} from "_src/engine/data_builders/normals_builder";
import workspace from "_src/workspace";

const _VEC3_TMP = vec3.create();

export default {

  smooth(object, chunks, strength) {
    smoothChunksVertices(chunks, strength);
    recalculateChunksVerticesNormals(chunks);
    for (const chunk of chunks) {
      workspace.dropChunkHash(object, object.frames[0], chunk);
    }
  }

}

function smoothChunksVertices(chunks, strength) {

  for (let chunk of chunks) {

    const geom = chunk.geometry;

    const parentVerticesMask = chunk.parentVerticesMask;

    laplacianSmooth(geom, chunk.dupliVertices, parentVerticesMask, strength);

    chunk.markAttributeDirty("a_position");

  }

}

function laplacianSmooth(geometry, dupliVertices, parentVerticesMask, strength ) {

  const positions = geometry.getItemBuffer("a_position");
  const selectionMask = geometry.getItemBuffer("active_selection_mask");

  for (let i = 0; i < selectionMask.length; ++i) {

    if (selectionMask[i] == 1 && parentVerticesMask[i] == 0) {
      smoothVert( i, geometry, positions, dupliVertices[i], strength );
    }

  }

  geometry.recalculateBoundings();

}

function smoothVert( originalVertInd, geom, positions, vertDuplicates, strength) {

  const originalOffset = 3 * originalVertInd;

  const posSum = _VEC3_TMP;

  const initX = positions[originalOffset];
  const initY = positions[originalOffset + 1];
  const initZ = positions[originalOffset + 2];

  posSum[0] = initX;
  posSum[1] = initY;
  posSum[2] = initZ;

  let numVerts = 1;

  numVerts += accumulateVertPositions( geom, positions, originalVertInd, posSum, true);

  if (vertDuplicates) {

    for (let i = 0; i < vertDuplicates.length; i += 2 ) {

      const chunk   = vertDuplicates[i];
      const vertInd = vertDuplicates[i+1];

      const neighbourGeom = chunk.geometry;
      const nPositions = neighbourGeom.getItemBuffer("a_position");

      numVerts += accumulateVertPositions( neighbourGeom, nPositions, vertInd, posSum, true);

    }

  }

  const invNumVerts = 1 / numVerts;

  const x = lerpValues(initX, posSum[0] * invNumVerts, strength);
  const y = lerpValues(initY, posSum[1] * invNumVerts, strength);
  const z = lerpValues(initZ, posSum[2] * invNumVerts, strength);

  positions[originalOffset] = x;
  positions[originalOffset + 1] = y;
  positions[originalOffset + 2] = z;

  if (vertDuplicates) {

    for (let i = 0; i < vertDuplicates.length; i += 2 ) {

      const chunk   = vertDuplicates[i];
      const neighVertOffset = 3 * vertDuplicates[i+1];

      const neighbourGeom = chunk.geometry;
      const neighPositions = neighbourGeom.getItemBuffer("a_position");

      neighPositions[neighVertOffset] = x;
      neighPositions[neighVertOffset + 1] = y;
      neighPositions[neighVertOffset + 2] = z;

    }

  }

}

function accumulateVertPositions( geom, positions, originalVertInd, posSum, includeBorder) {

  const indices = geom.getItemBuffer("indices");
  const E2e = geom.getItemBuffer("edges2edges");
  const V2e = geom.getItemBuffer("verts2edges");
  const borderMask = geom.getItemBuffer("border_mask");

  let numVerts = 0;

  let startID = V2e[originalVertInd];

  if (borderMask[startID] === -1) {

    if (includeBorder) {
      const vInd = indices[startID];
      const offset = 3 * vInd;
      posSum[0] += positions[offset];
      posSum[1] += positions[offset + 1];
      posSum[2] += positions[offset + 2];

      ++numVerts;
    }

    startID = startID % 3 < 2 ? startID + 1
                              : startID - 2;
  }

  let currID = startID;

  // do not process more than 16 vertices
  while (numVerts < 16) {

    // opposite
    const oppID = E2e[currID];

    if (borderMask[oppID] === -1) {

      if (includeBorder) {
        // next
        currID = currID % 3 < 2 ? currID + 1
                                : currID - 2;

        const vInd = indices[currID];
        const offset = 3 * vInd;

        posSum[0] += positions[offset];
        posSum[1] += positions[offset + 1];
        posSum[2] += positions[offset + 2];

        ++numVerts;
      }

      break;

    }

    const vInd = indices[oppID];
    const offset = 3 * vInd;

    posSum[0] += positions[offset];
    posSum[1] += positions[offset + 1];
    posSum[2] += positions[offset + 2];

    ++numVerts;

    // next
    currID = oppID % 3 < 2 ? oppID + 1
                           : oppID - 2;

    if (currID == startID) {
      break;
    }

  }

  return numVerts;

}

function recalculateChunksVerticesNormals(chunks) {

  for (let chunk of chunks) {

    const geom = chunk.geometry;

    const positions = geom.getItemBuffer("a_position");
    const indices = geom.getItemBuffer("indices");
    const selectionMask = geom.getItemBuffer("active_selection_mask");
    const normals = geom.getItemBuffer("a_normal");

    if (normals) {
      const normalize = false;
      rebuildVerticesNormalsMask(indices, positions, normals, selectionMask, normalize);
    }
  }

  for (const chunk of chunks) {
    const selectionMask = chunk.geometry.getItemBuffer("active_selection_mask");
    const normals = chunk.geometry.getItemBuffer("a_normal");

    if (normals) {
      chunk.averageNormals(chunk.dupliVertices, selectionMask);
    }
  }

}
